import { useCallback } from 'react';
import useAmplitude from 'providers/root/AmplitudeProvider/useAmplitude';

import type { CreateInvoiceDataDto } from '@subscriptions/application/dtos';
import { getSessionUseCases } from '@subscriptions/composition';

import type {
  AnalyticsStatus,
  GroupingAction,
  InvoiceDataSource,
  InvoicingChoice,
  InvoicingMethod,
  InvoicingSource,
  RetryScope,
  RetryStatus,
  TrackedPaymentMethod,
  WizardStep,
} from '@subscriptions/presentation/constants';

/** Flow the wizard was entered through, mirroring the origins the URL carries. */
type TrackedOrigin = 'create' | 'overdue-payment' | 'reactivation' | 'update-payment';

/** Keys declared under `$subscription` in the catalog. The union is what keeps a typo from
 * reaching Amplitude: the wrapper resolves an unknown path to `undefined` and emits it anyway. */
type SubscriptionEventKey =
  | 'detailViewed'
  | 'invoiceDataUpdated'
  | 'invoiceRetryFinished'
  | 'invoiceRetryStarted'
  | 'invoicingSelected'
  | 'paymentConfigurationFinished'
  | 'paymentConfigurationStarted'
  | 'paymentFinished'
  | 'paymentMethodSaved'
  | 'paymentStarted'
  | 'paymentStepViewed';

const SECTION_PATH = '@@section.subscription';

/** Tracking observes a flow, it does not take part in it: a throw here would surface as the
 * operation the call site just completed having failed. */
const safely = (emit: () => void) => {
  try {
    emit();
  } catch {
    // Dropped on purpose.
  }
};

type DetailViewedPayload = {
  blockUuid: string;
  /** Omitted when the block carries no state; an empty string reads as a value of its own. */
  customerState?: string;
  isGrouped: boolean;
  storesCount: number;
};

type PaymentStartedPayload = {
  blockUuid: string;
  customerState?: string;
  /** Omitted when the block has no payment date yet; a `0` would read as "charged today". */
  daysToPaymentDate: number | null;
  hasPaymentMethod: boolean;
  origin: TrackedOrigin;
};

type PaymentStepViewedPayload = {
  blockUuid: string;
  origin: TrackedOrigin;
  paymentMethod?: TrackedPaymentMethod;
  step: WizardStep;
};

type PaymentOutcomePayload = {
  blockUuid: string;
  origin: TrackedOrigin;
  paymentMethod: TrackedPaymentMethod;
  status: AnalyticsStatus;
};

type ConfigurationStartedPayload = {
  action: GroupingAction;
  /** Absent while grouping: the block does not exist yet. */
  blockUuid?: string;
  storesCount: number;
};

type ConfigurationFinishedPayload = {
  action: GroupingAction;
  blockUuid?: string;
  errorCode?: string;
  groupInvoices: boolean;
  status: AnalyticsStatus;
  storesAdded: number;
  storesRemoved: number;
};

type InvoicingSelectedPayload = {
  blockUuid: string;
  choice: InvoicingChoice;
  /** Absent when the choice was not to invoice. */
  method?: InvoicingMethod;
  source: InvoicingSource;
  status: AnalyticsStatus;
};

type InvoiceRetryStartedPayload = {
  blockUuid: string;
  hasFailureReason: boolean;
  scope: RetryScope;
};

type InvoiceRetryFinishedPayload = {
  blockUuid?: string;
  rejectedCount: number;
  scope: RetryScope;
  status: RetryStatus;
};

type InvoiceDataUpdatedPayload = {
  /** Names only: the values are the taxpayer's RFC, name and email. */
  changedFields: (keyof CreateInvoiceDataDto)[];
  source: InvoiceDataSource;
};

/** Amplitude tracking for the subscriptions domain: the only place that reaches the legacy
 * provider, so event names, the section and property shapes live here, not at each call site. */
export const useSubscriptionAnalytics = () => {
  const { logEvent } = useAmplitude();

  const track = useCallback(
    (event: SubscriptionEventKey, properties: Record<string, unknown>) => {
      safely(() => {
        // Read here and not at each call site: the block only knows its organization once it has a
        // payment method, so a failure on a brand-new block would emit without it and drop out of
        // every org-segmented chart.
        const organizationUuid = getSessionUseCases().getCurrentOrganizationUuid.execute();

        logEvent(`@event.$subscription.${event}`, SECTION_PATH, {
          ...properties,
          ...(organizationUuid ? { organization_uuid: organizationUuid } : {}),
        });
      });
    },
    [logEvent],
  );

  const trackDetailViewed = useCallback(
    ({ blockUuid, customerState, isGrouped, storesCount }: DetailViewedPayload) => {
      track('detailViewed', {
        block_uuid: blockUuid,
        is_grouped: isGrouped,
        stores_count: storesCount,
        ...(customerState ? { customer_state: customerState } : {}),
      });
    },
    [track],
  );

  const trackPaymentStarted = useCallback(
    ({ blockUuid, customerState, daysToPaymentDate, hasPaymentMethod, origin }: PaymentStartedPayload) => {
      track('paymentStarted', {
        block_uuid: blockUuid,
        has_payment_method: hasPaymentMethod,
        origin,
        ...(customerState ? { customer_state: customerState } : {}),
        ...(daysToPaymentDate === null ? {} : { days_to_payment_date: daysToPaymentDate }),
      });
    },
    [track],
  );

  const trackPaymentStepViewed = useCallback(
    ({ blockUuid, origin, paymentMethod, step }: PaymentStepViewedPayload) => {
      track('paymentStepViewed', {
        block_uuid: blockUuid,
        origin,
        step,
        ...(paymentMethod ? { payment_method: paymentMethod } : {}),
      });
    },
    [track],
  );

  const trackPaymentMethodSaved = useCallback(
    ({ blockUuid, origin, paymentMethod, status }: PaymentOutcomePayload) => {
      track('paymentMethodSaved', {
        block_uuid: blockUuid,
        origin,
        payment_method: paymentMethod,
        status,
      });
    },
    [track],
  );

  const trackPaymentFinished = useCallback(
    ({ blockUuid, origin, paymentMethod, status }: PaymentOutcomePayload) => {
      track('paymentFinished', {
        block_uuid: blockUuid,
        origin,
        payment_method: paymentMethod,
        status,
      });
    },
    [track],
  );

  const trackConfigurationStarted = useCallback(
    ({ action, blockUuid, storesCount }: ConfigurationStartedPayload) => {
      track('paymentConfigurationStarted', {
        action,
        stores_count: storesCount,
        ...(blockUuid ? { block_uuid: blockUuid } : {}),
      });
    },
    [track],
  );

  const trackConfigurationFinished = useCallback(
    ({
      action,
      blockUuid,
      errorCode,
      groupInvoices,
      status,
      storesAdded,
      storesRemoved,
    }: ConfigurationFinishedPayload) => {
      track('paymentConfigurationFinished', {
        action,
        group_invoices: groupInvoices,
        status,
        stores_added: storesAdded,
        stores_removed: storesRemoved,
        ...(blockUuid ? { block_uuid: blockUuid } : {}),
        ...(errorCode ? { error_code: errorCode } : {}),
      });
    },
    [track],
  );

  const trackInvoicingSelected = useCallback(
    ({ blockUuid, choice, method, source, status }: InvoicingSelectedPayload) => {
      track('invoicingSelected', {
        block_uuid: blockUuid,
        choice,
        source,
        status,
        ...(method ? { method } : {}),
      });
    },
    [track],
  );

  const trackInvoiceRetryStarted = useCallback(
    ({ blockUuid, hasFailureReason, scope }: InvoiceRetryStartedPayload) => {
      track('invoiceRetryStarted', {
        block_uuid: blockUuid,
        has_failure_reason: hasFailureReason,
        scope,
      });
    },
    [track],
  );

  const trackInvoiceRetryFinished = useCallback(
    ({ blockUuid, rejectedCount, scope, status }: InvoiceRetryFinishedPayload) => {
      track('invoiceRetryFinished', {
        rejected_count: rejectedCount,
        scope,
        status,
        ...(blockUuid ? { block_uuid: blockUuid } : {}),
      });
    },
    [track],
  );

  const trackInvoiceDataUpdated = useCallback(
    ({ changedFields, source }: InvoiceDataUpdatedPayload) => {
      safely(() => {
        // Amplitude identifies the store, so naming the person takes a property of its own.
        const userUuid = getSessionUseCases().getCurrentUserUuid.execute();

        track('invoiceDataUpdated', {
          changed_count: changedFields.length,
          changed_fields: changedFields,
          source,
          ...(userUuid ? { user_uuid: userUuid } : {}),
        });
      });
    },
    [track],
  );

  return {
    trackConfigurationFinished,
    trackConfigurationStarted,
    trackDetailViewed,
    trackInvoiceDataUpdated,
    trackInvoiceRetryFinished,
    trackInvoiceRetryStarted,
    trackInvoicingSelected,
    trackPaymentFinished,
    trackPaymentMethodSaved,
    trackPaymentStarted,
    trackPaymentStepViewed,
  };
};
